import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { InnerHero } from '@/components/sections'
import { Prefooter, Footer } from '@/components/layout'
import { LocalizedLink } from '@/components/LocalizedLink'

export function NashiPartnerPage() {
  const { t } = useTranslation('common')

  const partnerBenefits = [
    t('nashiPartner.benefit1'),
    t('nashiPartner.benefit2'),
    t('nashiPartner.benefit3'),
    t('nashiPartner.benefit4'),
  ]

  const partnerFeatures = [
    t('nashiPartner.feature1'),
    t('nashiPartner.feature2'),
    t('nashiPartner.feature3'),
    t('nashiPartner.feature4'),
  ]

  return (
    <main>
      <InnerHero
        eyebrow={t('nashiPartner.heroEyebrow')}
        title={t('nashiPartner.heroTitle')}
        titleItalic={t('nashiPartner.heroTitleItalic')}
        body={t('nashiPartner.heroBody')}
        breadcrumbs={[
          { label: t('nav.home'), href: '/it' },
          { label: t('nashiPartner.breadcrumb') },
        ]}
      />

      {/* Cosa sono */}
      <section className="bg-cream py-20 md:py-28 overflow-hidden">
        <div className="nashi-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1 }}
            >
              <span className="nashi-eyebrow">{t('nashiPartner.aboutEyebrow')}</span>
              <h2
                className="mt-6 font-display text-bruno"
                style={{
                  fontSize: 'clamp(1.875rem, 3vw, 2.75rem)',
                  lineHeight: 1.05,
                  letterSpacing: '-0.015em',
                }}
              >
                <span className="block">{t('nashiPartner.aboutTitle1')}</span>
                <span className="block italic font-light text-argan-deep">{t('nashiPartner.aboutTitle2')}</span>
              </h2>
              <p className="mt-6 text-bruno-soft leading-relaxed max-w-md">
                {t('nashiPartner.aboutBody')}
              </p>
              <div className="mt-8 space-y-4">
                {partnerFeatures.map((item) => (
                  <div key={item} className="flex items-center gap-3 text-sm text-bruno-soft">
                    <span className="w-1.5 h-1.5 rounded-full bg-argan-deep" />
                    {item}
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="/images/nashi-salon-experience.jpg"
                  alt={t('nashiPartner.aboutImageAlt')}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-cream-soft border border-bruno/10 p-6 max-w-[240px]">
                <p className="font-display italic text-bruno text-lg leading-snug">
                  {t('nashiPartner.aboutQuote')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trova il salone */}
      <section className="bg-cream-soft py-20 md:py-28">
        <div className="nashi-container text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9 }}
          >
            <span className="nashi-eyebrow">{t('nashiPartner.findEyebrow')}</span>
            <h2
              className="mt-6 font-display text-bruno mx-auto max-w-2xl"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3.5rem)',
                lineHeight: 1.05,
                letterSpacing: '-0.015em',
              }}
            >
              <span className="block">{t('nashiPartner.findTitle1')}</span>
              <span className="block italic font-light text-argan-deep">{t('nashiPartner.findTitle2')}</span>
            </h2>
            <p className="mt-6 text-bruno-soft leading-relaxed max-w-lg mx-auto">
              {t('nashiPartner.findBody')}
            </p>
            <LocalizedLink
              to="storeLocator"
              className="mt-10 inline-flex items-center gap-3 bg-bruno text-cream font-medium uppercase tracking-[0.28em] text-[11px] px-10 py-4 hover:bg-argan-deep transition-colors group"
            >
              {t('utility.storeLocator')}
              <ArrowRight
                className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1"
                strokeWidth={1.75}
              />
            </LocalizedLink>
          </motion.div>
        </div>
      </section>

      {/* Diventa Partner */}
      <section className="bg-cream py-20 md:py-28">
        <div className="nashi-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1 }}
              className="order-2 lg:order-1 relative"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="/images/stores/milano-dante.jpg"
                  alt={t('nashiPartner.joinImageAlt')}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-cream-soft border border-bruno/10 p-6 max-w-[240px]">
                <p className="font-display italic text-bruno text-lg leading-snug">
                  {t('nashiPartner.joinQuote')}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="order-1 lg:order-2"
            >
              <span className="nashi-eyebrow">{t('nashiPartner.joinEyebrow')}</span>
              <h2
                className="mt-6 font-display text-bruno"
                style={{
                  fontSize: 'clamp(1.875rem, 3vw, 2.75rem)',
                  lineHeight: 1.05,
                  letterSpacing: '-0.015em',
                }}
              >
                <span className="block">{t('nashiPartner.joinTitle1')}</span>
                <span className="block italic font-light text-argan-deep">{t('nashiPartner.joinTitle2')}</span>
              </h2>
              <p className="mt-6 text-bruno-soft leading-relaxed max-w-md">
                {t('nashiPartner.joinBody')}
              </p>
              <div className="mt-8 space-y-4">
                {partnerBenefits.map((item) => (
                  <div key={item} className="flex items-center gap-3 text-sm text-bruno-soft">
                    <span className="w-1.5 h-1.5 rounded-full bg-argan-deep" />
                    {item}
                  </div>
                ))}
              </div>
              <LocalizedLink
                to="diventaPartner"
                className="mt-10 inline-flex items-center gap-3 bg-bruno text-cream font-medium uppercase tracking-[0.28em] text-[11px] px-10 py-4 hover:bg-argan-deep transition-colors group"
              >
                {t('nashiPartner.joinCta')}
                <ArrowRight
                  className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1"
                  strokeWidth={1.75}
                />
              </LocalizedLink>
            </motion.div>
          </div>
        </div>
      </section>

      <Prefooter />
      <Footer />
    </main>
  )
}
